import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    TextField,
    Button,
    Box,
    Typography,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    InputAdornment,
    IconButton,
    Paper,
    Divider,
    Fade,
    Avatar
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SchoolIcon from '@mui/icons-material/School';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import LOCAL_OFFERIcon from '@mui/icons-material/LocalOffer';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import MapIcon from '@mui/icons-material/Map';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import api from '../../services/api';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';

interface EnrollmentModalProps {
    open: boolean;
    onClose: () => void;
    onCreated: (enrollment: any) => void;
}

const EnrollmentModal: React.FC<EnrollmentModalProps> = ({ open, onClose, onCreated }) => {
    const { user } = useAuth();
    const [organizations, setOrganizations] = useState<any[]>([]);
    const [learners, setLearners] = useState<any[]>([]);
    const [offers, setOffers] = useState<any[]>([]);
    const [parcoursList, setParcoursList] = useState<any[]>([]);

    const [organizationId, setOrganizationId] = useState('');
    const [learnerId, setLearnerId] = useState('');
    const [offerId, setOfferId] = useState('');
    const [parcoursId, setParcoursId] = useState('');
    const [startDate, setStartDate] = useState(new Date().toISOString().split('T')[0]);
    const [endDate, setEndDate] = useState('');
    const [minHours, setMinHours] = useState<number | ''>('');

    // For summary display
    const [selectedLearner, setSelectedLearner] = useState<any>(null);
    const [selectedParcours, setSelectedParcours] = useState<any>(null);

    const { showToast } = useToast();

    useEffect(() => {
        if (open) {
            fetchOrganizations();

            // Set organization if of-admin
            if (user?.role === 'of-admin' && user.organizationId) {
                const orgId = typeof user.organizationId === 'string' ? user.organizationId : user.organizationId._id;
                setOrganizationId(orgId);
                fetchOffers(null);
            } else {
                setOrganizationId('');
            }

            // Reset other fields
            setLearnerId('');
            setOfferId('');
            setParcoursId('');
            setStartDate(new Date().toISOString().split('T')[0]);
            setEndDate('');
            setMinHours('');
            setSelectedLearner(null);
            setSelectedParcours(null);
        }
    }, [open, user]);

    useEffect(() => {
        if (organizationId) {
            fetchOffers(organizationId);
            fetchLearners(organizationId);
        } else {
            setLearners([]);
            setLearnerId('');
            setSelectedLearner(null);
        }
    }, [organizationId]);

    useEffect(() => {
        if (offerId) {
            fetchParcours(offerId);
        } else {
            setParcoursList([]);
            setParcoursId('');
            setSelectedParcours(null);
        }
    }, [offerId]);

    // Update selected objects for summary
    useEffect(() => {
        if (learnerId) {
            const l = learners.find(l => l._id === learnerId);
            setSelectedLearner(l);
        }
    }, [learnerId, learners]);

    useEffect(() => {
        if (parcoursId) {
            const p = parcoursList.find(p => p._id === parcoursId);
            setSelectedParcours(p);
        }
    }, [parcoursId, parcoursList]);


    const fetchOrganizations = async () => {
        try {
            const res = await api.get('/api/organizations');
            setOrganizations(res.data);
        } catch (err) {
            console.error('Error fetching organizations:', err);
        }
    };

    const fetchLearners = async (orgId: string) => {
        try {
            const res = await api.get(`/api/users/organization/${orgId}?role=learner`);
            setLearners(res.data);
        } catch (err) {
            console.error('Error fetching learners:', err);
        }
    };

    const fetchOffers = async (orgId: string | null) => {
        try {
            const res = await api.get(orgId ? `/api/offer?organizationId=${orgId}` : `/api/offer`);
            const data = Array.isArray(res.data) ? res.data : [];
            setOffers(data);
            if (data.length === 1) {
                setOfferId(data[0]._id);
            }
        } catch (err) {
            console.error('Error fetching offers:', err);
            setOffers([]);
        }
    };

    const fetchParcours = async (offId: string) => {
        try {
            const res = await api.get(`/api/course/parcours?offer=${offId}`);
            setParcoursList(res.data);
            if (res.data && res.data.length === 1) {
                setParcoursId(res.data[0]._id);
            }
        } catch (err) {
            console.error('Error fetching parcours:', err);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const data = {
                learner: learnerId,
                parcours: parcoursId,
                startDate,
                endDate,
                minHours: minHours !== '' ? Number(minHours) : undefined
            };
            const res = await api.post('/api/enrollment', data);
            showToast('Inscription créée avec succès', 'success');
            onCreated(res.data);
        } catch (err: any) {
            console.error('Error creating enrollment:', err);
            showToast(err.response?.data?.message || 'Erreur lors de la création', 'error');
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 4,
                    overflow: 'hidden',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
                }
            }}
        >
            <Grid container sx={{ minHeight: '600px' }}>
                {/* Left Column - Forms */}
                <Grid size={{ md: 7 }} sx={{ p: 4, bgcolor: '#ffffff' }}>
                    <Box sx={{ mb: 4 }}>
                        <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'Quicksand', color: '#1a202c', mb: 1 }}>
                            Nouvelle Inscription
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#718096', fontFamily: 'Quicksand' }}>
                            Remplissez les informations ci-dessous pour inscrire un apprenant à un parcours.
                        </Typography>
                    </Box>

                    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                        {/* Section 1: Qui ? */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                1. L'Apprenant
                            </Typography>
                            <Grid container spacing={2}>
                                {user?.role !== 'of-admin' && (
                                    <Grid size={{ xs: 12, md: 6 }}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Organisation</InputLabel>
                                            <Select
                                                value={organizationId}
                                                label="Organisation"
                                                onChange={(e) => setOrganizationId(e.target.value)}
                                                startAdornment={
                                                    <InputAdornment position="start">
                                                        <BusinessIcon fontSize="small" color="action" />
                                                    </InputAdornment>
                                                }
                                                sx={{ borderRadius: 2 }}
                                            >
                                                {organizations.map((org) => (
                                                    <MenuItem key={org._id} value={org._id}>
                                                        {org.name}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid size={{ xs: 12, md: user?.role === 'of-admin' ? 12 : 6 }}>
                                    <FormControl fullWidth size="small" disabled={!organizationId}>
                                        <InputLabel>Apprenant</InputLabel>
                                        <Select
                                            value={learnerId}
                                            label="Apprenant"
                                            onChange={(e) => setLearnerId(e.target.value)}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <PersonIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            }
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {(learners && learners.length > 0) ? learners.map((learner) => (
                                                <MenuItem key={learner._id} value={learner._id}>
                                                    {learner.firstName} {learner.lastName}
                                                </MenuItem>
                                            )) : <MenuItem disabled>Aucun apprenant</MenuItem>}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed' }} />

                        {/* Section 2: Quoi ? */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                2. Le Parcours
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small">
                                        <InputLabel>Offre</InputLabel>
                                        <Select
                                            value={offerId}
                                            label="Offre"
                                            onChange={(e) => setOfferId(e.target.value)}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <LOCAL_OFFERIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            }
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {offers.map((offer) => (
                                                <MenuItem key={offer._id} value={offer._id}>
                                                    {offer.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <FormControl fullWidth size="small" disabled={!offerId}>
                                        <InputLabel>Parcours</InputLabel>
                                        <Select
                                            value={parcoursId}
                                            label="Parcours"
                                            onChange={(e) => setParcoursId(e.target.value)}
                                            startAdornment={
                                                <InputAdornment position="start">
                                                    <MapIcon fontSize="small" color="action" />
                                                </InputAdornment>
                                            }
                                            sx={{ borderRadius: 2 }}
                                        >
                                            {parcoursList.map((parcours) => (
                                                <MenuItem key={parcours._id} value={parcours._id}>
                                                    {parcours.title}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </Box>

                        <Divider sx={{ borderStyle: 'dashed' }} />

                        {/* Section 3: Quand ? */}
                        <Box>
                            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mb: 2, textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: '0.05em' }}>
                                3. Période
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Date de début"
                                        type="date"
                                        fullWidth
                                        size="small"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TextField
                                        label="Date de fin"
                                        type="date"
                                        fullWidth
                                        size="small"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 12 }}>
                                    <TextField
                                        label="Heures minimales (financeur)"
                                        type="number"
                                        fullWidth
                                        size="small"
                                        value={minHours}
                                        onChange={(e) => setMinHours(e.target.value === '' ? '' : Number(e.target.value))}
                                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                                    />
                                </Grid>
                            </Grid>
                        </Box>

                        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                            <Button onClick={onClose} sx={{ color: '#718096', fontWeight: 600 }}>
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={!learnerId || !parcoursId || !startDate || !endDate}
                                sx={{
                                    bgcolor: '#667eea',
                                    fontWeight: 700,
                                    boxShadow: '0 4px 6px -1px rgba(102, 126, 234, 0.5)',
                                    borderRadius: 2,
                                    px: 4,
                                    '&:hover': { bgcolor: '#5a67d8' }
                                }}
                            >
                                Valider l'inscription
                            </Button>
                        </Box>
                    </Box>
                </Grid>

                {/* Right Column - Summary */}
                <Grid size={{ md: 5 }} sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    p: 4,
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    position: 'relative',
                    overflow: 'hidden'
                }}>
                    <Box sx={{
                        position: 'absolute',
                        top: -20,
                        right: -20,
                        width: 150,
                        height: 150,
                        borderRadius: '50%',
                        background: 'rgba(255,255,255,0.1)',
                    }} />

                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <SchoolIcon /> Résumé
                    </Typography>

                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 3 }}>
                        {/* Learner Card */}
                        <Fade in={!!selectedLearner}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Apprenant</Typography>
                                {selectedLearner ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                        <Avatar sx={{ bgcolor: 'white', color: '#667eea' }}>{selectedLearner.firstName[0]}</Avatar>
                                        <Box>
                                            <Typography variant="subtitle1" fontWeight={600}>{selectedLearner.firstName} {selectedLearner.lastName}</Typography>
                                            <Typography variant="caption" sx={{ opacity: 0.8 }}>{selectedLearner.email}</Typography>
                                        </Box>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.5, fontStyle: 'italic' }}>Sélectionnez un apprenant...</Typography>
                                )}
                            </Paper>
                        </Fade>

                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            {selectedLearner && selectedParcours && <ArrowForwardIcon sx={{ opacity: 0.5 }} />}
                        </Box>

                        {/* Parcours Card */}
                        <Fade in={!!selectedParcours}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Parcours</Typography>
                                {selectedParcours ? (
                                    <Box sx={{ mt: 1 }}>
                                        <Typography variant="subtitle1" fontWeight={600}>{selectedParcours.title}</Typography>
                                        <Typography variant="caption" sx={{ opacity: 0.8 }}>{selectedParcours.description?.substring(0, 60)}...</Typography>
                                    </Box>
                                ) : (
                                    <Typography variant="body2" sx={{ mt: 1, opacity: 0.5, fontStyle: 'italic' }}>Sélectionnez un parcours...</Typography>
                                )}
                            </Paper>
                        </Fade>

                        {/* Dates Card */}
                        <Fade in={!!startDate && !!endDate}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', color: 'white', border: '1px solid rgba(255,255,255,0.2)' }}>
                                <Typography variant="caption" sx={{ opacity: 0.7, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Planification</Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                                    <CalendarTodayIcon fontSize="small" sx={{ opacity: 0.8 }} />
                                    <Box>
                                        <Typography variant="body2">Du <b>{new Date(startDate).toLocaleDateString('fr-FR')}</b></Typography>
                                        {endDate && <Typography variant="body2">Au <b>{new Date(endDate).toLocaleDateString('fr-FR')}</b></Typography>}
                                    </Box>
                                </Box>
                            </Paper>
                        </Fade>

                        {learnerId && parcoursId && startDate && endDate && (
                            <Fade in timeout={1000}>
                                <Box sx={{ mt: 'auto', textAlign: 'center' }}>
                                    <CheckCircleIcon sx={{ fontSize: 48, color: '#48bb78', mb: 1 }} />
                                    <Typography variant="body1" fontWeight={600}>Prêt à valider !</Typography>
                                </Box>
                            </Fade>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Dialog>
    );
};

export default EnrollmentModal;
